import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { FaChevronLeft, FaMinus, FaPlus, FaTimes } from 'react-icons/fa'
import { AiOutlineInbox } from 'react-icons/ai'

/**
 * Field app UI atoms (2026-11-04). Everything is styled by css/field-ops-app.css (classes fo-*): big touch targets,
 * phone first, tablet and desktop scale up. No optional chaining / nullish coalescing (CRA 2.1.3).
 */

export const Pill = ({ tone, icon, children, title }) => (
    <span className={'fo-pill fo-pill--' + (tone || 'gray')} title={title}>{icon ? <span className="fo-pill__icon">{icon}</span> : null}{children}</span>
)

/** Segmented control: items = [{ key, label, icon?, badge? }] */
export const Seg = ({ items, value, onChange, full }) => (
    <div className={'fo-seg' + (full ? ' fo-seg--full' : '')} role="tablist">
        {items.map((i) => (
            <button key={i.key} type="button" role="tab" aria-selected={value === i.key} className={'fo-seg__btn' + (value === i.key ? ' is-on' : '')} onClick={() => onChange(i.key)}>
                {i.icon ? <span className="fo-seg__icon">{i.icon}</span> : null}{i.label}
                {i.badge ? <span className="fo-seg__badge">{i.badge}</span> : null}
            </button>
        ))}
    </div>
)

/** Button. kind: primary | success | danger | ghost | soft (default soft). big = 56px tall. */
export const FoBtn = ({ kind, big, block, disabled, busy, onClick, icon, children, href, newTab, title, small }) => {
    const cls = 'fo-btn fo-btn--' + (kind || 'soft') + (big ? ' fo-btn--big' : '') + (block ? ' fo-btn--block' : '') + (small ? ' fo-btn--small' : '') + (disabled || busy ? ' is-disabled' : '')
    const inner = (
        <React.Fragment>
            {busy ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : (icon ? <span className="fo-btn__icon">{icon}</span> : null)}
            <span>{children}</span>
        </React.Fragment>
    )
    if (href && !disabled) return <a className={cls} href={href} target={newTab ? '_blank' : undefined} rel={newTab ? 'noopener noreferrer' : undefined} title={title}>{inner}</a>
    return <button type="button" className={cls} title={title} disabled={disabled || busy} onClick={disabled || busy ? undefined : onClick}>{inner}</button>
}

export const Banner = ({ tone, icon, title, children, action }) => (
    <div className={'fo-banner fo-banner--' + (tone || 'info')}>
        {icon ? <div className="fo-banner__icon">{icon}</div> : null}
        <div className="fo-banner__text">{title ? <strong>{title}</strong> : null}{title && children ? ' ' : null}{children}</div>
        {action ? <div className="fo-banner__action">{action}</div> : null}
    </div>
)

/** White card with an optional title row. */
export const Section = ({ title, right, children, tight, className, id }) => (
    <section id={id} className={'fo-card' + (tight ? ' fo-card--tight' : '') + (className ? ' ' + className : '')}>
        {title || right ? <div className="fo-card__head"><h3>{title}</h3>{right ? <div className="fo-card__right">{right}</div> : null}</div> : null}
        {children}
    </section>
)

export const KV = ({ label, children, strong }) => (
    <div className="fo-kv"><span className="fo-kv__l">{label}</span><span className={'fo-kv__v' + (strong ? ' is-strong' : '')}>{children}</span></div>
)

/** Progress ring. */
export const Ring = ({ value, max, size, stroke, color, label, sub }) => {
    const sz = size || 76
    const st = stroke || 8
    const r = (sz - st) / 2
    const c = 2 * Math.PI * r
    const p = max > 0 ? Math.min(1, value / max) : 0
    return (
        <div className="fo-ring" style={{ width: sz, height: sz }}>
            <svg width={sz} height={sz} viewBox={'0 0 ' + sz + ' ' + sz}>
                <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={st} />
                <circle cx={sz / 2} cy={sz / 2} r={r} fill="none" stroke={color || '#34d399'} strokeWidth={st} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - p)} transform={'rotate(-90 ' + sz / 2 + ' ' + sz / 2 + ')'} style={{ transition: 'stroke-dashoffset .4s' }} />
            </svg>
            <div className="fo-ring__in"><strong>{label}</strong>{sub ? <span>{sub}</span> : null}</div>
        </div>
    )
}

export const Bar = ({ value, max, tone }) => {
    const p = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
    return <div className="fo-bar"><div className={'fo-bar__fill fo-bar__fill--' + (tone || 'blue')} style={{ width: p + '%' }}></div></div>
}

export const Empty = ({ title, text, action }) => (
    <div className="fo-empty"><AiOutlineInbox className="fo-empty__icon" /><strong>{title}</strong>{text ? <span>{text}</span> : null}{action ? <div style={{ marginTop: 12 }}>{action}</div> : null}</div>
)

export const Avatar = ({ name, tone }) => {
    const parts = String(name || '?').replace(/[^A-Za-z ]/g, ' ').trim().split(/\s+/)
    const ini = ((parts[0] || '?')[0] + (parts[1] ? parts[1][0] : '')).toUpperCase()
    return <span className={'fo-avatar fo-avatar--' + (tone || 'blue')}>{ini}</span>
}

/** Quantity stepper with a typed value in the middle. */
export const QtyStepper = ({ value, onChange, min, max, label }) => {
    const v = Number(value) || 0
    const lo = min === undefined ? 0 : min
    const set = (n) => { let x = Math.max(lo, n); if (max !== undefined) x = Math.min(max, x); onChange(x) }
    return (
        <div className="fo-qty">
            <button type="button" aria-label={'Less' + (label ? ' ' + label : '')} onClick={() => set(v - 1)} disabled={v <= lo}><FaMinus /></button>
            <input type="number" inputMode="numeric" min={lo} value={v} onChange={(e) => set(Number(e.target.value) || 0)} aria-label={label || 'Quantity'} />
            <button type="button" aria-label={'More' + (label ? ' ' + label : '')} onClick={() => set(v + 1)}><FaPlus /></button>
        </div>
    )
}

/** Single (or multi) choice chips. options = ['text'] or [{ key, label }]. */
export const ChipSelect = ({ options, value, onChange, multi }) => (
    <div className="fo-chips">
        {options.map((o) => {
            const key = typeof o === 'string' ? o : o.key
            const label = typeof o === 'string' ? o : o.label
            const on = multi ? (value || []).indexOf(key) >= 0 : value === key
            return (
                <button key={key} type="button" className={'fo-chip' + (on ? ' is-on' : '')} aria-pressed={on}
                    onClick={() => { if (multi) { const cur = value || []; onChange(on ? cur.filter((x) => x !== key) : cur.concat([key])) } else onChange(on ? '' : key) }}>
                    {label}
                </button>
            )
        })}
    </div>
)

/** Radio-style cards: options = [{ key, title, text, tone? }] */
export const ChoiceCards = ({ options, value, onChange }) => (
    <div className="fo-choices">
        {options.map((o) => (
            <button key={o.key} type="button" className={'fo-choice' + (value === o.key ? ' is-on' : '') + (o.tone ? ' fo-choice--' + o.tone : '')} aria-pressed={value === o.key} onClick={() => onChange(o.key)}>
                <span className="fo-choice__dot"></span>
                <span className="fo-choice__body"><strong>{o.title}</strong>{o.text ? <span>{o.text}</span> : null}</span>
            </button>
        ))}
    </div>
)

export const TextArea = ({ value, onChange, placeholder, rows }) => (
    <textarea className="fo-input fo-input--area" rows={rows || 3} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
)

export const Field = ({ label, hint, error, children }) => (
    <div className="fo-field">
        {label ? <label className="fo-field__label">{label}</label> : null}
        {children}
        {error ? <div className="fo-field__error">{error}</div> : (hint ? <div className="fo-field__hint">{hint}</div> : null)}
    </div>
)

/**
 * Full-screen page pushed over the tabs (phone-style navigation). `footer` stays pinned at the bottom (primary action).
 * It sits under the portal's fixed menu: --fo-menu-h is published by FieldOpsApp.
 */
export const ScreenShell = ({ title, subtitle, onBack, right, children, footer, headerTone }) => {
    useEffect(() => {
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = prev }
    }, [])
    return (
        <div className="fo-screen" role="dialog" aria-label={title}>
            <div className={'fo-screen__bar' + (headerTone ? ' fo-screen__bar--' + headerTone : '')}>
                <button type="button" className="fo-screen__back" onClick={onBack} aria-label="Back"><FaChevronLeft /></button>
                <div className="fo-screen__title"><strong>{title}</strong>{subtitle ? <span>{subtitle}</span> : null}</div>
                {right ? <div className="fo-screen__right">{right}</div> : null}
            </div>
            <div className="fo-screen__body"><div className="fo-screen__inner">{children}</div></div>
            {footer ? <div className="fo-screen__foot"><div className="fo-screen__footInner">{footer}</div></div> : null}
        </div>
    )
}

/** Bottom sheet on phones, centered dialog on larger screens. */
export const Sheet = ({ show, title, subtitle, onClose, children, footer, tall }) => {
    useEffect(() => {
        if (!show) return undefined
        const onKey = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [show, onClose])
    if (!show) return null
    // rendered on <body>: sheets opened from a full screen would otherwise stay under the portal's fixed top menu
    return ReactDOM.createPortal((
        <div className="fo-sheet" role="dialog" aria-label={title}>
            <div className="fo-sheet__scrim" onClick={onClose}></div>
            <div className={'fo-sheet__panel' + (tall ? ' fo-sheet__panel--tall' : '')}>
                <div className="fo-sheet__grab"></div>
                <div className="fo-sheet__head">
                    <div><strong>{title}</strong>{subtitle ? <span>{subtitle}</span> : null}</div>
                    <button type="button" onClick={onClose} aria-label="Close"><FaTimes /></button>
                </div>
                <div className="fo-sheet__body">{children}</div>
                {footer ? <div className="fo-sheet__foot">{footer}</div> : null}
            </div>
        </div>
    ), document.body)
}

/** Confirmation with an optional reason picker (used for skip, cancel check-in, far check-in...). */
export const ConfirmSheet = ({ show, title, subtitle, text, reasons, requireReason, confirmLabel, tone, noteLabel, onConfirm, onClose }) => {
    const [reason, setReason] = useState('')
    const [note, setNote] = useState('')
    const [tried, setTried] = useState(false)
    const wasShown = useRef(false)
    useEffect(() => { if (show && !wasShown.current) { setReason(''); setNote(''); setTried(false) } wasShown.current = show }, [show])
    const need = reasons && requireReason !== false
    const submit = () => { setTried(true); if (need && !reason) return; onConfirm({ reason: reason, note: note }) }
    return (
        <Sheet show={show} title={title} subtitle={subtitle} onClose={onClose}
            footer={<React.Fragment><FoBtn kind="ghost" onClick={onClose}>Cancel</FoBtn><FoBtn kind={tone === 'danger' ? 'danger' : 'primary'} onClick={submit}>{confirmLabel || 'Confirm'}</FoBtn></React.Fragment>}>
            {text ? <p className="fo-sheet__text">{text}</p> : null}
            {reasons ? <Field label="Reason" error={tried && need && !reason ? 'Pick a reason' : ''}><ChipSelect options={reasons} value={reason} onChange={setReason} /></Field> : null}
            {noteLabel !== null ? <Field label={noteLabel || 'Note (optional)'}><TextArea value={note} onChange={setNote} rows={2} placeholder="Add details" /></Field> : null}
        </Sheet>
    )
}
